// =============================================================================
// Materials Price Book — faithful port of IMS Materials_Price_Book +
// Print_Price_Book / PrintPriceBook_Report. Filter by inventory type, toggle
// each item's "In Price Book" flag (legacy InPriceBook), and print the book
// (items flagged in_price_book) grouped by type with code, description & price.
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { Printer, BookOpen, ClipboardList } from 'lucide-react'
import { listProductsByType, listInventoryTypes, listPriceBookItems, updateProduct } from '../services/data'
import { PageHeader, Card, Spinner, Button } from '../components/shared/ui'
import { currency, productCode } from '../lib/format'
import { printHtml } from '../lib/print'
import { THOMSONS_LOGO } from '../lib/assets'
import type { Product } from '../types'
import { toast } from 'react-toastify'

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export default function PriceBook() {
  const [types, setTypes] = useState<string[]>([])
  const [type, setType] = useState('')
  const [rows, setRows] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async (t: string) => {
    setLoading(true)
    try { setRows(await listProductsByType(t || undefined)) }
    catch (e) { console.error(e); toast.error('Failed to load products') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { listInventoryTypes().then(setTypes).catch(() => {}) }, [])
  useEffect(() => { load(type) }, [load, type])

  const toggle = async (p: Product) => {
    const next = !p.in_price_book
    setRows(rs => rs.map(r => r.id === p.id ? { ...r, in_price_book: next } : r))
    try { await updateProduct(p.id, { in_price_book: next }) }
    catch (e) { console.error(e); toast.error('Failed to update'); setRows(rs => rs.map(r => r.id === p.id ? { ...r, in_price_book: !next } : r)) }
  }

  const print = async () => {
    let items: Product[]
    try { items = await listPriceBookItems() } catch { toast.error('Failed to load price book'); return }
    if (items.length === 0) { toast.info('No items are flagged for the price book yet.'); return }
    const byType = new Map<string, Product[]>()
    for (const p of items) {
      const k = p.inventory_type || 'Other'
      if (!byType.has(k)) byType.set(k, [])
      byType.get(k)!.push(p)
    }
    const sections = [...byType.entries()].map(([t, ps]) => `
      <h3>${esc(t)}</h3>
      <table><thead><tr><th>Code</th><th>Description</th><th class="right">Retail</th><th class="right">Wholesale</th></tr></thead>
      <tbody>${ps.map(p => `<tr><td>${esc(productCode(p))}</td><td>${esc(p.name)}</td><td class="right">${currency(p.retail_price)}</td><td class="right">${currency(p.wholesale_price ?? p.retail_price)}</td></tr>`).join('')}</tbody></table>`).join('')
    printHtml('Materials Price Book', `
      <div style="text-align:center"><img src="${THOMSONS_LOGO}" style="width:90px;height:auto" alt=""/><h2>Thomson's Landscaping — Materials Price Book</h2><p>${new Date().toLocaleDateString()}</p></div>
      ${sections}`,
      `h3 { margin: 16px 0 4px; border-bottom: 2px solid #2f7d32; color: #1f5e22; }
       table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
       th, td { text-align: left; padding: 3px 6px; border-bottom: 1px solid #ddd; font-size: 12px; }
       .right { text-align: right; }`)
  }

  // Count Sheet (legacy CountSheet_Report): a printable physical-count sheet for
  // the currently filtered items — code, description, on-hand, and a blank column.
  const printCountSheet = () => {
    if (rows.length === 0) { toast.info('No products to count'); return }
    const body = `
      <div style="text-align:center"><img src="${THOMSONS_LOGO}" style="width:90px;height:auto" alt=""/>
        <h2>Inventory Count Sheet${type ? ` — ${esc(type)}` : ''}</h2>
        <p>${new Date().toLocaleDateString()}</p></div>
      <table><thead><tr><th>Code</th><th>Description</th><th>Type</th><th class="right">System Qty</th><th class="count">Counted</th></tr></thead>
      <tbody>${rows.map(p => `<tr><td>${esc(productCode(p))}</td><td>${esc(p.name)}</td><td>${esc(p.inventory_type || '')}</td><td class="right">${p.qty_on_hand ?? 0}</td><td class="count"></td></tr>`).join('')}</tbody></table>`
    printHtml('Count Sheet', body,
      `h2 { margin: 4px 0; } table { width: 100%; border-collapse: collapse; margin-top: 10px; }
       th, td { text-align: left; padding: 5px 6px; border: 1px solid #999; font-size: 12px; }
       .right { text-align: right; } .count { width: 90px; }`)
  }

  const inBook = rows.filter(r => r.in_price_book).length

  return (
    <div>
      <PageHeader title="Materials Price Book" subtitle="Flag items for the price book and print it"
        actions={<>
          <Button variant="secondary" onClick={printCountSheet}><ClipboardList size={16} /> Count Sheet</Button>
          <Button onClick={print}><Printer size={16} /> Print Price Book</Button>
        </>} />

      <Card className="p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-gray-600">Inventory type</label>
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={type} onChange={e => setType(e.target.value)}>
            <option value="">All types</option>
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-sm text-gray-500 ml-auto flex items-center gap-1"><BookOpen size={15} /> {inBook} of {rows.length} shown are in the book</span>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-6"><Spinner label="Loading…" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-600">
                  <th className="text-center px-3 py-2.5 font-semibold w-20">In Book</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Code</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Description</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Type</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Retail</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Wholesale</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">No products found.</td></tr>
                ) : rows.map(p => (
                  <tr key={p.id} className={`border-b border-gray-100 ${p.in_price_book ? 'bg-green-50/60' : ''}`}>
                    <td className="px-3 py-1.5 text-center">
                      <input type="checkbox" className="h-4 w-4" checked={!!p.in_price_book} onChange={() => toggle(p)} />
                    </td>
                    <td className="px-3 py-1.5 font-medium text-gray-900">{productCode(p)}</td>
                    <td className="px-3 py-1.5 text-gray-700">{p.name}</td>
                    <td className="px-3 py-1.5 text-gray-500">{p.inventory_type || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{currency(p.retail_price)}</td>
                    <td className="px-3 py-1.5 text-right text-gray-500">{currency(p.wholesale_price ?? p.retail_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
